import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../App';

function StatCard({ title, value, subtitle, color, icon }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      padding: '20px 24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</p>
          <div style={{ fontSize: 32, fontWeight: 700, color: color || '#1a1a2e' }}>{value}</div>
          {subtitle && <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{subtitle}</p>}
        </div>
        {icon && (
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: `${color}15` || '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24
          }}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}

function SmallStatCard({ title, value, color, icon }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 10,
      padding: '16px 20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: 16
    }}>
      <div style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: `${color}15` || '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: color || '#1a1a2e' }}>{value}</div>
        <p style={{ fontSize: 12, color: '#666' }}>{title}</p>
      </div>
    </div>
  );
}

function QuickActionButton({ label, icon, onClick, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 20px',
        background: color || '#0066cc',
        color: 'white',
        border: 'none',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onMouseOver={e => e.target.style.opacity = '0.9'}
      onMouseOut={e => e.target.style.opacity = '1'}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      {label}
    </button>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api('/admin/stats'),
      api('/admin/users?limit=5'),
      api('/admin/requests?limit=5')
    ])
      .then(([statsData, usersData, requestsData]) => {
        setStats(statsData);
        setRecentUsers(usersData.data || []);
        setRecentRequests(requestsData.data || []);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid #e0e0e0',
          borderTop: '3px solid #0066cc',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }} />
        <p style={{ color: '#666' }}>Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#dc3545' }}>
        Failed to load dashboard data
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      active: '#0066cc',
      matched: '#17a2b8',
      confirmed: '#28a745',
      in_progress: '#6f42c1',
      completed: '#28a745',
      cancelled: '#dc3545'
    };
    return colors[status] || '#666';
  };

  const getUserTypeLabel = (type) => {
    const labels = {
      yacht_owner: 'Yacht Owner',
      helper: 'Helper',
      admin: 'Admin'
    };
    return labels[type] || type;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Dashboard</h1>
        <p style={{ color: '#666', fontSize: 14 }}>Welcome to 1crew3 Admin Panel</p>
      </div>

      {/* Top Stats Grid - 2x4 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard
          title="Total Users"
          value={stats.users?.total || 0}
          color="#0066cc"
          icon="👥"
        />
        <StatCard
          title="Active Users"
          value={stats.users?.active || stats.users?.total || 0}
          color="#28a745"
          icon="✓"
        />
        <StatCard
          title="Total Requests"
          value={stats.requests?.total || 0}
          color="#6f42c1"
          icon="📋"
        />
        <StatCard
          title="Active Requests"
          value={stats.requests?.active || 0}
          color="#ffc107"
          icon="⚡"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard
          title="Total Crew"
          value={stats.users?.crew || stats.users?.helpers || 0}
          subtitle="Professional crew members"
          color="#17a2b8"
          icon="⚓"
        />
        <StatCard
          title="Mooring Helpers"
          value={stats.users?.helpers || 0}
          subtitle="Available for mooring"
          color="#20c997"
          icon="🚢"
        />
        <StatCard
          title="Pending Verifications"
          value={stats.verifications?.pending || 0}
          subtitle="Email verifications"
          color="#dc3545"
          icon="📧"
        />
        <StatCard
          title="Avg Rating"
          value={stats.ratings?.average || '0.0'}
          subtitle={`${stats.ratings?.total || 0} total reviews`}
          color="#f59e0b"
          icon="⭐"
        />
      </div>

      {/* Quick Actions */}
      <div style={{
        background: 'white',
        borderRadius: 12,
        padding: 20,
        marginBottom: 24,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600 }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <QuickActionButton
            label="View Analytics"
            icon="📈"
            color="#0066cc"
            onClick={() => navigate('/analytics')}
          />
          <QuickActionButton
            label="Verify Emails"
            icon="✅"
            color="#28a745"
            onClick={() => navigate('/verifications')}
          />
          <QuickActionButton
            label="Send Notification"
            icon="📨"
            color="#6f42c1"
            onClick={() => navigate('/messaging')}
          />
          <QuickActionButton
            label="Manage Users"
            icon="👥"
            color="#17a2b8"
            onClick={() => navigate('/users')}
          />
        </div>
      </div>

      {/* Recent Users and Requests */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Recent Users */}
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Recent Users</h3>
            <Link to="/users" style={{ fontSize: 13, color: '#0066cc', textDecoration: 'none' }}>View all →</Link>
          </div>
          {recentUsers.length > 0 ? (
            <div>
              {recentUsers.map((user, idx) => (
                <Link
                  key={user.id}
                  to={`/users/${user.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: idx < recentUsers.length - 1 ? '1px solid #eee' : 'none',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: user.profile_photo_url ? `url(${user.profile_photo_url}) center/cover` : '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 12,
                    fontSize: 16,
                    color: '#666'
                  }}>
                    {!user.profile_photo_url && (user.first_name?.[0] || '?')}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {user.first_name} {user.last_name}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>{user.email}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 500,
                      background: user.user_type === 'helper' ? '#28a74515' : '#0066cc15',
                      color: user.user_type === 'helper' ? '#28a745' : '#0066cc',
                    }}>
                      {getUserTypeLabel(user.user_type)}
                    </span>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      {formatDate(user.created_at)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No recent users</div>
          )}
        </div>

        {/* Recent Requests */}
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Recent Requests</h3>
            <Link to="/requests" style={{ fontSize: 13, color: '#0066cc', textDecoration: 'none' }}>View all →</Link>
          </div>
          {recentRequests.length > 0 ? (
            <div>
              {recentRequests.map((request, idx) => (
                <div
                  key={request.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: idx < recentRequests.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 12,
                    fontSize: 18
                  }}>
                    {request.request_type === 'realtime' || request.request_type === 'real_time' ? '⚡' : '📅'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {request.port_name || 'Unknown Port'}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {request.yacht_name || 'Unknown Yacht'} • {request.scheduled_date}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 500,
                      background: `${getStatusColor(request.status)}15`,
                      color: getStatusColor(request.status),
                    }}>
                      {request.status}
                    </span>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      {request.helpers_confirmed || 0}/{request.helpers_needed || 1} helpers
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No recent requests</div>
          )}
        </div>
      </div>

      {/* Bottom Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <SmallStatCard
          title="Completed"
          value={stats.requests?.completed || 0}
          color="#28a745"
          icon="✓"
        />
        <SmallStatCard
          title="Conversations"
          value={stats.conversations?.total || 0}
          color="#17a2b8"
          icon="💬"
        />
        <SmallStatCard
          title="Total Ratings"
          value={stats.ratings?.total || 0}
          color="#f59e0b"
          icon="⭐"
        />
        <SmallStatCard
          title="Cancelled"
          value={stats.requests?.cancelled || 0}
          color="#dc3545"
          icon="✕"
        />
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
